import React, { useState } from "react";
import Loading from "../../../../components/loading";
import { BtnSubmit } from "../../../../components/button/button-submit/button-submit";
import TextInput from "../../../../components/inputs/text";
import SelectInput from "../../../../components/inputs/select";
import CheckboxInput from "../../../../components/inputs/check";
import AlertMessage from "../../../../components/alert-message";
import libForm from "../../../../util/libForm";
import { IndexHook } from "./hooks";
import { ToastContainer } from "react-toastify";
import { Paintbrush, CreditCard, Truck, Globe, Search } from "lucide-react";

const FormStoreCatalog = () => {
	const { register, handleSubmit, control, errors, isSubmitting, loading, watch, handleOnSubmit } = IndexHook();
	const [activeTab, setActiveTab] = useState("aparencia");

	return (
		<>
			{loading ? (
				<Loading mensagem="Carregando configurações..." />
			) : (
				<form className="form" onSubmit={handleSubmit(handleOnSubmit)} style={{ display: "flex", flexDirection: "column", height: "calc(100vh - 195px)" }}>
					<main className="overflow-auto flex-grow-1">
						<div className="row p-4">
							{/* Navegação de Abas */}
							<div className="col-12 mb-4">
								<div className="flex bg-slate-100 p-1 rounded-lg">
									<button
										type="button"
										onClick={() => setActiveTab("aparencia")}
										className={`flex items-center justify-center gap-2 flex-1 py-2.5 px-4 text-sm font-medium rounded-md transition-all ${
											activeTab === "aparencia" ? "bg-white shadow-sm text-primary" : "text-slate-500 hover:bg-slate-200"
										}`}
									>
										<Paintbrush className="h-4 w-4" />
										Aparência e SEO
									</button>
									<button
										type="button"
										onClick={() => setActiveTab("operacao")}
										className={`flex items-center justify-center gap-2 flex-1 py-2.5 px-4 text-sm font-medium rounded-md transition-all ${
											activeTab === "operacao" ? "bg-white shadow-sm text-primary" : "text-slate-500 hover:bg-slate-200"
										}`}
									>
										<Truck className="h-4 w-4" />
										Entregas e Pagamentos
									</button>
								</div>
							</div>

							{/* CONTEÚDO: APARÊNCIA E SEO */}
							{activeTab === "aparencia" && (
								<>
									<div className="col-12 mb-4 border-b pb-2">
										<h5 className="text-lg font-semibold text-slate-800">Aparência do Catálogo</h5>
										<p className="text-sm text-slate-500">Configure o visual principal da sua loja.</p>
									</div>

									<SelectInput
										label="Tema / Cores do Catálogo"
										id="theme"
										labelSelect="nome"
										control={control}
										errors={errors}
										data={[
											{ theme: "red", nome: "Vermelho" },
											{ theme: "blue", nome: "Azul" },
											{ theme: "dark", nome: "Escuro" },
										]}
										className="col-12 col-md-4 mb-4"
									/>

									<div className="col-12 mb-4 mt-2 border-b pb-2">
										<h5 className="text-lg font-semibold text-slate-800">Configurações de Busca (SEO)</h5>
										<p className="text-sm text-slate-500">Otimize as palavras-chave para melhorar o posicionamento nos buscadores.</p>
									</div>

									<AlertMessage type="info" title="Apareça no Google">
										Configure títulos e descrições atrativas para que sua loja seja encontrada facilmente em pesquisas na internet.
									</AlertMessage>

									<div className="col-12 col-md-6 mb-3 mt-3">
										<TextInput
											label="Título da Loja no Buscador"
											id="titulo_seo"
											placeholder="Ex: Minha Loja - As melhores roupas da cidade"
											register={register}
											errors={errors}
											onKeyDown={libForm.EnterTab}
										/>
									</div>

									<div className="col-12 mb-3">
										<TextInput
											label="Resumo/Descrição no Buscador"
											id="descricao_seo"
											placeholder="Ex: Compre roupas e acessórios com os melhores preços..."
											register={register}
											errors={errors}
											onKeyDown={libForm.EnterTab}
										/>
									</div>

									{/* Google Preview */}
									<div className="col-12 mt-4">
										<div className="border border-slate-200 rounded-xl bg-white shadow-sm overflow-hidden">
											<div className="bg-slate-50 px-4 py-2.5 border-b border-slate-100 flex items-center justify-between">
												<h6 className="text-[11px] font-bold tracking-wider text-slate-500 uppercase flex items-center gap-1.5 m-0">
													<Search className="w-3.5 h-3.5 text-blue-500" />
													Como os clientes verão sua loja no Google
												</h6>
											</div>
											<div className="p-5">
												<div className="flex items-center gap-3 mb-1.5">
													<div className="w-7 h-7 rounded-full bg-slate-100 flex items-center justify-center border border-slate-200 overflow-hidden">
														<Globe className="w-4 h-4 text-slate-500" />
													</div>
													<div className="flex flex-col">
														<span className="text-[14px] text-[#202124] font-normal leading-tight">Nome da Sua Loja</span>
														<span className="text-[12px] text-[#4d5156] leading-tight tracking-wide">https://seucatalogo.com.br</span>
													</div>
												</div>
												<h3 className="text-[20px] text-[#1a0dab] font-normal mb-1 truncate hover:underline cursor-pointer pt-1">
													{watch("titulo_seo") || "Nome da Sua Loja Aqui"}
												</h3>
												<p className="text-[14px] text-[#4d5156] leading-[1.58] line-clamp-2 w-full max-w-[600px]">
													{watch("descricao_seo") || "A descrição do seu negócio aparecerá aqui e ajudará os clientes a entenderem o que você vende."}
												</p>
											</div>
										</div>
									</div>
								</>
							)}

							{/* CONTEÚDO: ENTREGAS E PAGAMENTOS */}
							{activeTab === "operacao" && (
								<>
									<div className="col-12 mb-4 border-b pb-2">
										<h5 className="text-lg font-semibold text-slate-800">Entregas e Regras de Venda</h5>
										<p className="text-sm text-slate-500">Defina como os clientes recebem os pedidos e as regras do seu catálogo.</p>
									</div>

									<div className="col-12 d-flex flex-wrap gap-3 mb-4">
										<div className="p-3 border rounded-lg bg-gray-50 flex-grow-1 transition-colors hover:border-primary/50">
											<CheckboxInput
												label="Entrega via Transportadora"
												id="entrega_transportadora"
												register={register}
												errors={errors}
											/>
										</div>
										<div className="p-3 border rounded-lg bg-gray-50 flex-grow-1 transition-colors hover:border-primary/50">
											<CheckboxInput
												label="Entrega Local / Delivery"
												id="entrega_local"
												register={register}
												errors={errors}
											/>
										</div>
										<div className="p-3 border rounded-lg bg-gray-50 flex-grow-1 transition-colors hover:border-primary/50">
											<CheckboxInput
												label="Venda com Complementos"
												id="complementos"
												register={register}
												errors={errors}
											/>
										</div>
										<div className="p-3 border rounded-lg bg-gray-50 flex-grow-1 transition-colors hover:border-primary/50">
											<CheckboxInput
												label="Validar Estoque"
												id="validar_estoque"
												register={register}
												errors={errors}
											/>
										</div>
									</div>

									<div className="col-12 mb-3">
										<AlertMessage type="info" title="Como você entrega?">
											Habilite "Transportadora" para envios por correio/jadlog, ou "Delivery" para entregas locais via motoboy. Se você vende produtos adicionais (ex: borda recheada, acompanhamentos), ative a "Venda com Complementos".
										</AlertMessage>
									</div>

									<div className="col-12 mt-4 mb-4 border-b pb-2">
										<h5 className="text-lg font-semibold text-slate-800">Formas de Pagamento Ativas</h5>
										<p className="text-sm text-slate-500">Defina os métodos de pagamento que serão aceitos no seu catálogo digital.</p>
									</div>

									<div className="col-12 d-flex flex-wrap gap-3 mb-4">
										<div className="p-3 border rounded-lg bg-gray-50 flex-grow-1 transition-colors hover:border-primary/50">
											<CheckboxInput label="Dinheiro" id="pgto_dinheiro" register={register} errors={errors} />
										</div>
										<div className="p-3 border rounded-lg bg-gray-50 flex-grow-1 transition-colors hover:border-primary/50">
											<CheckboxInput label="PIX" id="pgto_pix" register={register} errors={errors} />
										</div>
										<div className="p-3 border rounded-lg bg-gray-50 flex-grow-1 transition-colors hover:border-primary/50">
											<CheckboxInput label="Cartão de Crédito" id="pgto_credito" register={register} errors={errors} />
										</div>
										<div className="p-3 border rounded-lg bg-gray-50 flex-grow-1 opacity-60">
											<CheckboxInput label="Boleto (Indisponível)" id="pgto_boleto" register={register} errors={errors} disabled={true} />
										</div>
									</div>

									<div className="col-12 mb-3">
										<AlertMessage type="info" title="Recebimentos no Catálogo">
											Marque acima quais formas de pagamento o cliente poderá selecionar ao finalizar o pedido. O sistema exibirá apenas as opções ativadas aqui.
										</AlertMessage>
									</div>
								</>
							)}
						</div>
					</main>

					<footer style={{ position: "sticky", bottom: 0, background: "white", borderTop: "1px solid #dee2e6", padding: "12px 16px", zIndex: 10 }}>
						<BtnSubmit isSubmitting={isSubmitting} />
					</footer>
				</form>
			)}
			<ToastContainer />
		</>
	);
};

export default FormStoreCatalog;
